import { SoundOutlined, StopOutlined } from '@ant-design/icons';
import { Button, message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TextToSpeech.scss';

const TextToSpeech = ({ text, title, disabled = false, size = 'middle' }) => {
  const { t } = useTranslation('global');
  const [isPlaying, setIsPlaying] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [speechSynthesis, setSpeechSynthesis] = useState(null);

  useEffect(() => {
    // Проверяем поддержку Web Speech API
    if ('speechSynthesis' in window) {
      setIsSupported(true);
      setSpeechSynthesis(window.speechSynthesis);
    } else {
      setIsSupported(false);
    }

    // Очищаем при размонтировании компонента
    return () => {
      if (window.speechSynthesis) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  const cleanText = (text) => {
    if (!text) return '';

    const withoutTags = text.replace(/<[^>]*>/g, ' ');

    const decoded = withoutTags
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&ldquo;/g, ' "')
      .replace(/&rdquo;/g, '" ')
      .replace(/&lsquo;/g, ' \'')
      .replace(/&rsquo;/g, '\' ')
      .replace(/&hellip;/g, '...')
      .replace(/&mdash;/g, ' — ')
      .replace(/&ndash;/g, ' – ');

    return decoded
      .replace(/\s+/g, ' ')
      .replace(/\n+/g, '. ')
      .replace(/\r+/g, '')
      .replace(/[•·●]/g, '. ')
      .trim();
  };

  const getTextToSpeak = () => {
    const cleanTitle = cleanText(title);
    const cleanTextContent = cleanText(text);

    if (cleanTitle && cleanTextContent) {
      return `${cleanTitle}. ${cleanTextContent}`;
    } else if (cleanTitle) {
      return cleanTitle;
    } else {
      return cleanTextContent;
    }
  };

  const speak = () => {
    if (!isSupported) {
      message.error(t('voice.playbackNotSupported'));
      return;
    }

    const textToSpeak = getTextToSpeak();

    if (!textToSpeak || textToSpeak.trim() === '') {
      message.warning(t('voice.noTextToPlay'));
      return;
    }

    // Останавливаем предыдущее воспроизведение
    if (speechSynthesis.speaking) {
      speechSynthesis.cancel();
    }

    const utterance = new SpeechSynthesisUtterance();

    // Настройки голоса
    utterance.text = getTextToSpeak();
    utterance.lang = 'ru-RU'; // Русский язык
    utterance.rate = 0.9; // Скорость речи (0.1 - 10)
    utterance.pitch = 1; // Высота голоса (0 - 2)
    utterance.volume = 0.8; // Громкость (0 - 1)

    // Попытка найти русский голос
    const voices = speechSynthesis.getVoices();
    const russianVoice = voices.find(voice =>
      voice.lang.startsWith('ru') ||
      voice.name.includes('Russian') ||
      voice.name.includes('русский')
    );

    if (russianVoice) {
      utterance.voice = russianVoice;
    }

    // События
    utterance.onstart = () => {
      setIsPlaying(true);
    };

    utterance.onend = () => {
      setIsPlaying(false);
    };

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event.error);
      setIsPlaying(false);
      message.error(t('voice.playbackError'));
    };

    utterance.onpause = () => {
      setIsPlaying(false);
    };

    utterance.onresume = () => {
      setIsPlaying(true);
    };

    // Начинаем воспроизведение
    speechSynthesis.speak(utterance);
  };

  const stop = () => {
    if (speechSynthesis) {
      speechSynthesis.cancel();
      setIsPlaying(false);
    }
  };

  if (!isSupported) {
    return null;
  }

  return (
    <Button
      type={isPlaying ? 'primary' : 'default'}
      icon={isPlaying ? <StopOutlined /> : <SoundOutlined />}
      onClick={isPlaying ? stop : speak}
      disabled={disabled || !text || text.trim() === ''}
      size={size}
      title={isPlaying ? t('voice.stopPlayback') : t('voice.listenPetition')}
      className={`text-to-speech-button ${isPlaying ? 'playing' : ''}`}
    >
      {isPlaying ? t('voice.stopPlayback') : t('voice.playback')}
    </Button>
  );
};

export default TextToSpeech;